import React from 'react';
import PropTypes from 'prop-types';

import SegmentAnalytics from '../../helpers/SegmentAnalytics.js';


import Marker from 'react-draggable';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

const markerPositions = {
  cornerMarkers: {
    topLeft: 'top-left',
    topRight: 'top-right',
    bottomLeft: 'bottom-left',
    bottomRight: 'bottom-right',
  },
  midMarkers: {
    top: 'top',
    right: 'right',
    bottom: 'bottom',
    left: 'left'
  }
};

const gridLines = {
  vertical1: 'vertical-1',
  vertical2: 'vertical-2',
  horizontal1: 'horizontal-1',
  horizontal2: 'horizontal-2'
};

class ImageCropper extends React.Component {
  imageLoadErrorMessage = 'The image file is invalid or corrupt. Please try a different image.';

  timeOpened = undefined;
  userCroppedImage = false;
  image = undefined;
  imageWrapperEl = undefined;
  imageInfo = { displayWidth: 0, displayHeight: 0, trueWidth: 0, trueHeight: 0 };
  fullSizeBoundingBox = { left: 0, top: 0, width: 0, height: 0 };
  maxBounds = { left: 0, top: 0, right: 0, bottom: 0 };

  constructor(props) {
    super(props);

    this.state = this.getInitialState();

    // Needed so we can remove the window event listener
    this.onResize = this.onResize.bind(this);
    this.preventScroll = this.preventScroll.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.imageData !== this.props.imageData) {
      this.setState(this.getInitialState());
    }
  }

  componentDidMount() {
    this.timeOpened = new Date().getTime();
    this.addWindowEventListeners();
  }

  componentWillUnmount() {
    const timeSpentOpen = (new Date().getTime() - this.timeOpened) / 1000;

    if (!this.userCroppedImage) {
      SegmentAnalytics.track('Image not cropped', { timeSpentOpen });
    } else {
      SegmentAnalytics.track('Image cropped', { timeSpentOpen });
    }

    this.removeWindowEventListeners();

    clearAllBodyScrollLocks();
  }

  handleCropperTouchStart = () => {
    this.preventScrolling = true;
  }

  handleCropperTouchEnd = () => {
    this.preventScrolling = false;
  }

  render() {
    return(
      <div className={`image-cropper ${this.props.className}`}
        onTouchStart={this.handleCropperTouchStart}
        onTouchEnd={this.handleCropperTouchEnd}
      >
        <div
          className="image-cropper__image-wrapper"
          ref={(el) => this.imageWrapperEl = el}
        >
          <img
            className="image-cropper__image"
            src={this.props.imageData}
            onLoad={this.onImageLoaded.bind(this)}
            onError={this.onImageLoadError.bind(this)}
            ref={(el) => this.imageEl = el}
          />

          <div
            className="image-cropper__bounding-box"
            style={{
              left: this.state.boundingBox.left,
              top: this.state.boundingBox.top,
              width: this.state.boundingBox.width,
              height: this.state.boundingBox.height
            }}
          >
            { this.props.showGridLines &&
              <span className="image-cropper__bounding-gridlines">
                { Object.keys(gridLines).map((key) =>
                  <span
                    key={key}
                    className={`image-cropper__bounding-gridlines__${gridLines[key]}`}
                  />
                )}
              </span>
            }
          </div>

          { this.props.showOverlay &&
            <div className="image-cropper__bounding-overlay">
              <span
                style={{
                  left: this.state.boundingBox.left,
                  top: this.state.boundingBox.top,
                  width: this.state.boundingBox.width,
                  height: this.state.boundingBox.height
                }} />
            </div>
          }

          <span className="image-cropper__bounding-markers">
            {this.props.showWarningSign && (
              <span
                className="image-cropper__warning-sign"
                style={{
                  left: this.state.markers['topLeft'].x,
                  top: this.state.markers['topLeft'].y
                }}
              >
                !
              </span>
            )}

            { this.props.showCornerMarkers &&
              Object.keys(markerPositions.cornerMarkers).map((key) =>
                <span
                  className={`image-cropper__bounding-marker__${markerPositions.cornerMarkers[key]}`}
                  key={key}
                  style={{
                    left: this.state.markers[key].x,
                    top: this.state.markers[key].y
                  }}
                />
              )
            }

            { this.props.showMidMarkers &&
              Object.keys(markerPositions.midMarkers).map((key) =>
                <span
                  className={`image-cropper__bounding-marker__${markerPositions.midMarkers[key]}`}
                  key={key}
                  style={{
                    left: this.state.markers[key].x,
                    top: this.state.markers[key].y
                  }}
                />
              )
            }
          </span>

          { this.state.boundingBoxInitialized &&
            <span className="image-cropper__bounding-handles">
              <Marker
                bounds={{
                  left: this.maxBounds.left,
                  top: this.maxBounds.top,
                  right: this.maxBounds.right - this.state.boundingBox.width,
                  bottom: this.maxBounds.bottom - this.state.boundingBox.height
                }}
                position={{
                  x: this.state.boundingBox.left,
                  y: this.state.boundingBox.top,
                }}
                onDrag={this.onMarkerDrag.bind(this, 'main')}
                onStart={this.onMarkerStart.bind(this)}
                onStop={this.onMarkerStop.bind(this)}
              >
                <span
                  className="image-cropper__bounding-handle__main"
                  style={{
                    width: this.state.boundingBox.width,
                    height: this.state.boundingBox.height
                  }}
                />
              </Marker>

              { this.props.showCornerMarkers &&
                Object.keys(markerPositions.cornerMarkers).map((key) =>
                  <Marker
                    key={key}
                    bounds={this.maxBounds}
                    position={{
                      x: this.state.markers[key].x,
                      y: this.state.markers[key].y,
                    }}
                    onDrag={this.onMarkerDrag.bind(this, key)}
                    onStart={this.onMarkerStart.bind(this)}
                    onStop={this.onMarkerStop.bind(this)}
                  >
                    <span className={`image-cropper__bounding-handle__${markerPositions.cornerMarkers[key]}`} />
                  </Marker>
                )
              }

              { this.props.showMidMarkers &&
                Object.keys(markerPositions.midMarkers).map((key) =>
                  <Marker
                    key={key}
                    bounds={this.maxBounds}
                    position={{
                      x: this.state.markers[key].x,
                      y: this.state.markers[key].y,
                    }}
                    onDrag={this.onMarkerDrag.bind(this, key)}
                    onStart={this.onMarkerStart.bind(this)}
                    onStop={this.onMarkerStop.bind(this)}
                  >
                    <span className={`image-cropper__bounding-handle__${markerPositions.midMarkers[key]}`} />
                  </Marker>
                )
              }
            </span>
          }
        </div>
      </div>
    );
  }

  calcImageInfo() {
    this.imageInfo = {
      displayWidth: parseInt(this.imageWrapperEl.offsetWidth),
      displayHeight: parseInt(this.imageWrapperEl.offsetHeight),
      trueWidth: parseInt(this.image.width),
      trueHeight: parseInt(this.image.height)
    };
  }

  onImageLoaded() {
    if (this.state.imageLoaded) { return; }

    this.image = new Image();
    this.image.src = this.props.imageData;

    this.calcImageInfo();
    this.calcMaxBounds();

    const boundingBox = this.fullSizeBoundingBoxToRelative(this.props.boundingBox) || this.calcInitialBoundingBox();
    const markers = this.updateMarkers(boundingBox);

    this.fullSizeBoundingBox = this.relativeBoundingBoxToFullSize(boundingBox);

    this.props.onBoundingBoxChange(this.fullSizeBoundingBox);

    this.setState({
      imageLoaded: true,
      boundingBoxInitialized: true,
      boundingBox: boundingBox,
      markers: markers
    });
  }

  onImageLoadError() {
    if (this.props.onImageLoadError) {
      this.props.onImageLoadError(this.imageLoadErrorMessage);
    }
  }

  calcMaxBounds() {
    this.maxBounds = {
      left: 0,
      top: 0,
      right: this.imageInfo.displayWidth,
      bottom: this.imageInfo.displayHeight
    };
  }

  calcInitialBoundingBox() {
    const maxDimension = Math.min(this.imageInfo.displayWidth, this.imageInfo.displayHeight);

    return {
      left: (this.imageInfo.displayWidth - maxDimension) / 2,
      top: (this.imageInfo.displayHeight - maxDimension) / 2,
      width: maxDimension,
      height: maxDimension
    };
  }

  onMarkerDrag(position, event, draggableEvent) {
    if (this.props.disabled) return;

    const { x, y } = draggableEvent;

    let bounding = this.getSimplifiedBounding();

    this.updateBoundingWithNewCoordinates(bounding, position, x, y);

    const boundingBox = this.updateBoundingBox(bounding);

    this.fullSizeBoundingBox = this.relativeBoundingBoxToFullSize(boundingBox);

    const markers = this.updateMarkers(boundingBox);

    this.setState({ boundingBox, markers });
  }

  onMarkerStart(event) {
    if (this.props.disabled) return;

    if (this.props.disableBodyScroll) {
      disableBodyScroll(event.target);
    }
  }

  onMarkerStop(event) {
    if (this.props.disabled) return;

    if (this.props.disableBodyScroll) {
      enableBodyScroll(event.target);
    }
    if (!this.userCroppedImage) { this.userCroppedImage = true; }
    this.props.onBoundingBoxChange(this.fullSizeBoundingBox);
  }

  // Basic bounding coordinates in order to make calculations simpler
  getSimplifiedBounding() {
    let boundingBox = this.state.boundingBox;

    return {
      topLeft: {
        x: boundingBox.left,
        y: boundingBox.top
      },
      bottomRight: {
        x: boundingBox.left + boundingBox.width,
        y: boundingBox.top + boundingBox.height
      }
    };
  }

  relativeBoundingBoxToFullSize(boundingBox) {
    if (!boundingBox) return;

    const left = parseInt((this.imageInfo.trueWidth / this.imageInfo.displayWidth) * boundingBox.left);
    const top = parseInt((this.imageInfo.trueHeight / this.imageInfo.displayHeight) * boundingBox.top);
    const width = parseInt((boundingBox.width * this.imageInfo.trueWidth) / this.imageInfo.displayWidth);
    const height = parseInt((boundingBox.height * this.imageInfo.trueHeight) / this.imageInfo.displayHeight);

    return { left, top, width, height };
  }

  fullSizeBoundingBoxToRelative(fullSizeBoundingBox) {
    if (!fullSizeBoundingBox) return;

    const left = parseInt((this.imageInfo.displayWidth / this.imageInfo.trueWidth) * fullSizeBoundingBox.left);
    const top = parseInt((this.imageInfo.displayHeight / this.imageInfo.trueHeight) * fullSizeBoundingBox.top);
    const width = parseInt((this.imageInfo.displayWidth * fullSizeBoundingBox.width) / this.imageInfo.trueWidth);
    const height = parseInt((this.imageInfo.displayHeight * fullSizeBoundingBox.height) / this.imageInfo.trueHeight);

    return { left, top, width, height };
  }

  updateBoundingWithNewCoordinates(bounding, position, x, y) {
    const originalBoundingWidth = bounding.bottomRight.x - bounding.topLeft.x;
    const originalBoundingHeight = bounding.bottomRight.y - bounding.topLeft.y;

    // Calculate and update basic bounding coordinates
    switch (position) {
    case 'main':
      bounding.topLeft.x = x;
      bounding.topLeft.y = y;
      bounding.bottomRight.x = originalBoundingWidth + x;
      bounding.bottomRight.y = originalBoundingHeight + y;
      break;
    case 'topLeft':
      bounding.topLeft.x = x;
      bounding.topLeft.y = y;
      break;
    case 'topRight':
      bounding.topLeft.y = y;
      bounding.bottomRight.x = x;
      break;
    case 'bottomLeft':
      bounding.topLeft.x = x;
      bounding.bottomRight.y = y;
      break;
    case 'bottomRight':
      bounding.bottomRight.x = x;
      bounding.bottomRight.y = y;
      break;
    case 'top':
      bounding.topLeft.y = y;
      break;
    case 'right':
      bounding.bottomRight.x = x;
      break;
    case 'bottom':
      bounding.bottomRight.y = y;
      break;
    case 'left':
      bounding.topLeft.x = x;
      break;
    }

    this.preventBoundingSwap(bounding, position);

    // Constraint updated bounding coordinates to a square
    if (this.props.constraintToSquare) {
      this.constraintBoundingToSquare(bounding, position);
    }
  }

  preventBoundingSwap(bounding, position) {
    const minBoundingSize = parseInt(
      (this.imageInfo.displayWidth * this.props.minBoundingSize) / this.imageInfo.trueWidth
    );

    if ((bounding.bottomRight.x < (bounding.topLeft.x + minBoundingSize)
      || bounding.bottomRight.y < (bounding.topLeft.y + minBoundingSize))) {
      switch(position) {
      case 'topLeft':
        bounding.topLeft.x = bounding.bottomRight.x - minBoundingSize;
        bounding.topLeft.y = bounding.bottomRight.y - minBoundingSize;
        break;
      case 'topRight':
        bounding.bottomRight.x = bounding.topLeft.x + minBoundingSize;
        bounding.topLeft.y = bounding.bottomRight.y - minBoundingSize;
        break;
      case 'bottomLeft':
        bounding.topLeft.x = bounding.bottomRight.x - minBoundingSize;
        bounding.bottomRight.y = bounding.topLeft.y + minBoundingSize;
        break;
      case 'bottomRight':
        bounding.bottomRight.x = bounding.topLeft.x + minBoundingSize;
        bounding.bottomRight.y = bounding.topLeft.y + minBoundingSize;
        break;
      case 'left':
        bounding.topLeft.x = bounding.bottomRight.x - minBoundingSize;
        break;
      case 'right':
        bounding.bottomRight.x = bounding.topLeft.x + minBoundingSize;
        break;
      case 'top':
        bounding.topLeft.y = bounding.bottomRight.y - minBoundingSize;
        break;
      case 'bottom':
        bounding.bottomRight.y = bounding.topLeft.y + minBoundingSize;
        break;
      }
    }
  }

  constraintBoundingToSquare(bounding, position) {
    const boundingWidth = bounding.bottomRight.x - bounding.topLeft.x;
    const boundingHeight = bounding.bottomRight.y - bounding.topLeft.y;
    const minDimension = Math.min(boundingWidth, boundingHeight);
    const horizontalCenter = ((bounding.bottomRight.x - bounding.topLeft.x) / 2) + bounding.topLeft.x;
    const verticalCenter = ((bounding.bottomRight.y - bounding.topLeft.y) / 2) + bounding.topLeft.y;

    switch (position) {
    case 'topLeft':
      bounding.topLeft.x = bounding.bottomRight.x - minDimension;
      bounding.topLeft.y = bounding.bottomRight.y - minDimension;
      break;
    case 'topRight':
      bounding.bottomRight.x = bounding.topLeft.x + minDimension;
      bounding.topLeft.y = bounding.bottomRight.y - minDimension;
      break;
    case 'bottomLeft':
      bounding.topLeft.x = bounding.bottomRight.x - minDimension;
      bounding.bottomRight.y = bounding.topLeft.y + minDimension;
      break;
    case 'bottomRight':
      bounding.bottomRight.x = bounding.topLeft.x + minDimension;
      bounding.bottomRight.y = bounding.topLeft.y + minDimension;
      break;
    case 'top':
    case 'bottom':
      bounding.topLeft.x = horizontalCenter - (boundingHeight / 2);
      bounding.bottomRight.x = horizontalCenter + (boundingHeight / 2);
      break;
    case 'left':
    case 'right':
      bounding.topLeft.y = verticalCenter - (boundingWidth / 2);
      bounding.bottomRight.y = verticalCenter + (boundingWidth / 2);
      break;
    }

    this.ensureBoundingIsWithinLimits(bounding, position);
  }

  ensureBoundingIsWithinLimits(bounding, position) {
    const maxBoundsLength = Math.min(this.maxBounds.right, this.maxBounds.bottom);

    // First pass: making sure no point goes over the maxBounds
    if (bounding.topLeft.y < 0) { bounding.topLeft.y = 0;}
    if (bounding.topLeft.x < 0) { bounding.topLeft.x = 0; }

    if (bounding.bottomRight.x > this.maxBounds.right) { bounding.bottomRight.x = this.maxBounds.right; }
    if (bounding.bottomRight.y > this.maxBounds.bottom) { bounding.bottomRight.y = this.maxBounds.bottom; }

    const boundingWidth = bounding.bottomRight.x - bounding.topLeft.x;
    const boundingHeight = bounding.bottomRight.y - bounding.topLeft.y;

    // Second pass: making sure we don't break the aspect ratio
    if (boundingWidth > maxBoundsLength ||
      boundingHeight > maxBoundsLength) {
      switch(position) {
      case 'left':
        bounding.topLeft.x = bounding.bottomRight.x - maxBoundsLength;
        break;
      case 'right':
        bounding.bottomRight.x = bounding.topLeft.x + maxBoundsLength;
        break;
      case 'top':
        bounding.topLeft.y = bounding.bottomRight.y - maxBoundsLength;
        break;
      case 'bottom':
        bounding.bottomRight.y = bounding.topLeft.y + maxBoundsLength;
        break;
      }
    }

    return bounding;
  }

  updateBoundingBox(bounding) {
    // Translate calculated bounding coordinates to the actual bounding box
    return {
      left: bounding.topLeft.x,
      top: bounding.topLeft.y,
      width: bounding.bottomRight.x - bounding.topLeft.x,
      height: bounding.bottomRight.y - bounding.topLeft.y
    };
  }

  updateMarkers(boundingBox) {
    return {
      topLeft: {
        x: boundingBox.left,
        y: boundingBox.top
      },
      topRight: {
        x: boundingBox.left + boundingBox.width,
        y: boundingBox.top
      },
      bottomLeft: {
        x: boundingBox.left,
        y: boundingBox.top + boundingBox.height
      },
      bottomRight: {
        x: boundingBox.left + boundingBox.width,
        y: boundingBox.top + boundingBox.height
      },
      top: {
        x: boundingBox.left + (boundingBox.width / 2),
        y: boundingBox.top
      },
      right: {
        x: boundingBox.left + boundingBox.width,
        y: boundingBox.top + (boundingBox.height / 2)
      },
      bottom: {
        x: boundingBox.left + (boundingBox.width / 2),
        y: boundingBox.top + boundingBox.height
      },
      left: {
        x: boundingBox.left,
        y: boundingBox.top + (boundingBox.height / 2)
      }
    };
  }

  getInitialState() {
    return {
      boundingBoxInitialized: false,
      imageLoaded: false,
      boundingBox: { left: 0, top: 0, width: 0, height: 0 },
      markers: {
        top: { x: 0, y: 0 },
        right: { x: 0, y: 0 },
        bottom: { x: 0, y: 0 },
        left: { x: 0, y: 0 },
        topLeft: { x: 0, y: 0 },
        topRight: { x: 0, y: 0 },
        bottomLeft: { x: 0, y: 0 },
        bottomRight: { x: 0, y: 0 }
      }
    };
  }

  onResize() {
    this.calcImageInfo();
    this.calcMaxBounds();

    const boundingBox = this.fullSizeBoundingBoxToRelative(this.fullSizeBoundingBox);
    const markers = this.updateMarkers(boundingBox);

    this.setState({ boundingBox, markers });
  }

  preventScroll(event) {
    if (!this.preventScrolling) return;

    event.preventDefault();
  }

  addWindowEventListeners() {
    window.addEventListener('resize', this.onResize);
    // must be added here and not on demand
    window.addEventListener('touchmove', this.preventScroll, {passive: false});
  }

  removeWindowEventListeners() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('touchmove', this.preventScroll);
  }
}

ImageCropper.defaultProps = {
  className: '',
  constraintToSquare: true,
  disableBodyScroll: true,
  disabled: false,
  enableAnalytics: true,
  showGridLines: false,
  showCornerMarkers: true,
  showMidMarkers: false,
  showOverlay: true,
  showWarningSign: false,
  minBoundingSize: 300
};

ImageCropper.propTypes = {
  className: PropTypes.string,
  constraintToSquare: PropTypes.bool,
  disabled: PropTypes.bool,
  disableBodyScroll: PropTypes.bool,
  enableAnalytics: PropTypes.bool,
  showGridLines: PropTypes.bool,
  showCornerMarkers: PropTypes.bool,
  showMidMarkers: PropTypes.bool,
  showOverlay: PropTypes.bool,
  showWarningSign: PropTypes.bool,
  imageData: PropTypes.any.isRequired,
  minBoundingSize: PropTypes.number,
  boundingBox: PropTypes.object,
  onBoundingBoxChange: PropTypes.func,
  onImageLoadError: PropTypes.func
};

export default ImageCropper;
